import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, finalize, map, shareReplay, switchMap, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { APP_PLATFORM, ROLE, STORAGE_KEYS } from '../constants/app.constants';
import { AdminUser, AuthUser, LoginRequest, LoginResponse, userDisplayName, userRoleId, userRoleIds } from '../models';
import { ApiService } from './api.service';

/**
 * Autenticación del panel contra `POST /auth/login`.
 *
 * Usa el auth moderno del backend: `jwt` como Bearer (15 min) y `refreshToken`
 * rotativo (30 días). El backend ya NO acepta `adminUserId` en el body/query
 * como credencial, así que el token es la única identidad válida.
 */
/** ¿La sesión tiene el rol ADMIN? Tolera sesiones guardadas sin `roleIds`. */
export function isAdmin(user: AuthUser | null | undefined): boolean {
  return !!user && userRoleIds(user).includes(ROLE.admin);
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(ApiService);

  /** Usuario actual (signal privado + expuesto de solo lectura). */
  private readonly _user = signal<AuthUser | null>(this.restoreUser());
  readonly user = this._user.asReadonly();

  /** Derivado: hay sesión activa. */
  readonly isAuthenticated = computed(() => this._user() !== null);

  /**
   * Refresh en curso, compartido por todas las peticiones que fallen a la vez:
   * el backend rota el refresh token y detecta reuso, así que dos refrescos en
   * paralelo revocarían TODAS las sesiones del usuario.
   */
  private refreshInFlight: Observable<string> | null = null;

  /** Id del admin autenticado (para las vistas; el backend lo toma del token). */
  adminId(): number {
    return this._user()?.id ?? 0;
  }

  /**
   * Autentica con la cuenta ADMIN de ese correo y valida que lo sea.
   *
   * `POST /auth/login` no devuelve el usuario, así que tras guardar el token se
   * consulta `GET /auth/token` («¿quién soy?») para conocer identidad y rol.
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    // `platform` le dice al backend desde donde se entra: cada app tiene sus
    // cuentas y el backend usa la cuenta ADMIN de ese correo.
    // La comprobacion de abajo se queda como red de seguridad para versiones
    // del backend que aun no la apliquen.
    return this.api.post<unknown>('auth/login', { ...credentials, platform: APP_PLATFORM }).pipe(
      switchMap((res) => this.resolveSession(res)),
      map((session) => {
        if (!isAdmin(session.user)) {
          // No es admin: se descartan los tokens guardados para leer el perfil.
          this.clearTokens();
          throw new Error('Esta cuenta no tiene permisos de administrador.');
        }
        return session;
      }),
    );
  }

  /** Guarda los tokens del login y resuelve la identidad del usuario. */
  private resolveSession(res: unknown): Observable<LoginResponse> {
    const body = (res ?? {}) as Record<string, unknown>;
    const data = ((body['data'] as Record<string, unknown>) ?? body) as Record<string, unknown>;

    // Se prefiere el JWT; `accessToken`/`token` son el token opaco legacy.
    const token = String(data['jwt'] ?? data['accessToken'] ?? data['token'] ?? '');
    if (!token) {
      return throwError(() => new Error('El servidor no devolvió un token de acceso.'));
    }

    localStorage.setItem(environment.tokenKey, token);
    const refreshToken = data['refreshToken'];
    if (refreshToken) {
      localStorage.setItem(environment.refreshTokenKey, String(refreshToken));
    }

    return this.api.get<unknown>('auth/token').pipe(
      map((profile) => {
        const user = ((profile ?? {}) as Record<string, unknown>)['user'] as AdminUser | undefined;
        if (!user || Number(user.id) <= 0) {
          this.clearTokens();
          throw new Error('No se pudo obtener el perfil del usuario tras iniciar sesión.');
        }
        return { token, user: this.toAuthUser(user) };
      }),
    );
  }

  /**
   * Renueva el JWT con el refresh token. Single-flight: las llamadas
   * concurrentes comparten la misma petición.
   */
  refreshSession(): Observable<string> {
    if (this.refreshInFlight) return this.refreshInFlight;

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No hay refresh token para renovar la sesión.'));
    }

    this.refreshInFlight = this.api.post<unknown>('auth/refresh', { refreshToken }).pipe(
      map((res) => {
        const body = (res ?? {}) as Record<string, unknown>;
        const jwt = String(body['jwt'] ?? body['accessToken'] ?? '');
        if (!jwt) throw new Error('El servidor no devolvió un token renovado.');

        localStorage.setItem(environment.tokenKey, jwt);
        // El refresh token se reemplaza SIEMPRE: el anterior queda revocado.
        const rotated = body['refreshToken'];
        if (rotated) localStorage.setItem(environment.refreshTokenKey, String(rotated));
        return jwt;
      }),
      finalize(() => {
        this.refreshInFlight = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.refreshInFlight;
  }

  /** Normaliza un usuario del backend a la forma mínima de sesión. */
  private toAuthUser(u: AdminUser): AuthUser {
    return {
      id: Number(u.id ?? 0),
      name: userDisplayName(u),
      email: u.email ?? '',
      roleId: userRoleId(u),
      roleIds: userRoleIds(u),
    };
  }

  /** Persiste la sesión tras un login correcto. */
  setSession(response: LoginResponse): void {
    localStorage.setItem(environment.tokenKey, response.token);
    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(response.user));
    this._user.set(response.user);
  }

  /** Devuelve el JWT almacenado (o null). */
  getToken(): string | null {
    return localStorage.getItem(environment.tokenKey);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(environment.refreshTokenKey);
  }

  /**
   * Cierra sesión: revoca el refresh token en el servidor (best-effort) y
   * limpia el almacenamiento local.
   */
  logout(): void {
    const refreshToken = this.getRefreshToken();
    if (refreshToken) {
      // Se lanza ANTES de limpiar para que el interceptor aún adjunte el Bearer.
      this.api
        .post<unknown>('auth/logout', { refreshToken })
        .subscribe({ next: () => undefined, error: () => undefined });
    }
    this.clearSession();
  }

  /** Limpia la sesión local sin llamar al servidor (p. ej. tras un 401). */
  clearSession(): void {
    this.clearTokens();
    localStorage.removeItem(STORAGE_KEYS.user);
    this._user.set(null);
  }

  private clearTokens(): void {
    localStorage.removeItem(environment.tokenKey);
    localStorage.removeItem(environment.refreshTokenKey);
    this.refreshInFlight = null;
  }

  private restoreUser(): AuthUser | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.user);
      return raw ? (JSON.parse(raw) as AuthUser) : null;
    } catch {
      return null;
    }
  }
}
